import "./style.scss";
import MultiplierSvg from "@/src/svg/CustomUtils/MultiplierSvg";
import ApproveSvg from "@/src/svg/CustomUtils/ApproveSvg";
import CloseSvg from "@/src/svg/CustomUtils/CloseSvg";
import ArrowSvg from "@/src/svg/autoCompile/ArrowSvg";
import CalendarSvg from "@/src/svg/CustomUtils/CalendarSvg";
import { useEffect, useMemo, useRef, useState } from "react";
import { officeAccountingPageRequests } from "@/src/api/officeAccountingPageRequests";
import { IOfficeAccountingIncomeCreatePayload, IOfficeAccountingIncomeRecord } from "@/src/types/officeAccounting";
import { formatOfficeAccountingIncomeRecordsTotal } from "@/src/utils/officeAccountingMappers";
import { formatOfficeAccountingUsdAmount, parseOfficeAccountingAmount } from "@/src/utils/officeAccountingTotalAmountUsd";
import { useFormatDateDisplay } from "@/src/hooks/useFormatDateDisplay";
import { usePushNotifications } from "@/src/components/CustomUtils/PushNotifications/PushNotificationsProvider";

export type CustomIncomeManagementMode = "invitation" | "payment";

type Props = {
  title: string;
  mode: CustomIncomeManagementMode;
  accountingId?: number;
  records?: IOfficeAccountingIncomeRecord[];
  invoiceOptions?: string[];
  onRefresh?: () => void | Promise<void>;
  onClose: () => void;
};

const ACCOUNT_OPTIONS = ["IEU PKO", "IEU Consaltens", "Main account"];
const CURRENCY_OPTIONS = ["USD", "EUR", "UAH"] as const;
type IncomeCurrency = (typeof CURRENCY_OPTIONS)[number];

/** Fallback: how many USD for 1 unit of currency. */
const FALLBACK_USD_PER_UNIT: Record<IncomeCurrency, number> = {
  USD: 1,
  EUR: 1.08,
  UAH: 0.024,
};

const getTodayDate = () => {
  const date = new Date();
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

const formatCoefficientInput = (rate: number): string => {
  if (!Number.isFinite(rate) || rate <= 0) return "x1.00";
  const digits = rate >= 1 ? 2 : 4;
  const trimmed = rate.toFixed(digits).replace(/\.?0+$/, "");
  return `x${trimmed || "1"}`;
};

type IncomeFormState = Omit<IOfficeAccountingIncomeCreatePayload, "applicant_accounting_id">;

const emptyForm = (usdPerUnit: Record<IncomeCurrency, number> = FALLBACK_USD_PER_UNIT): IncomeFormState => ({
  date: getTodayDate(),
  invoice_number: "",
  count: "",
  currency: "USD",
  coefficient: formatCoefficientInput(usdPerUnit.USD),
  account: "",
  result: "",
  note: "",
});

const parseNumericInput = (raw: string): number | null => {
  const cleaned = String(raw ?? "")
    .replace(/x/gi, "")
    .replace(/\s/g, "")
    .replace(/,/g, ".")
    .trim();
  if (!cleaned) return null;
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatResultInput = (amount: number): string => {
  const [intPart, decPart] = amount.toFixed(2).split(".");
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${withSpaces}.${decPart}`;
};

const formatCountDisplay = (count: string, currency: string): string => {
  const amount = parseOfficeAccountingAmount(count);
  const label = currency.trim() || "USD";
  if (!String(count ?? "").trim()) return `0.00 ${label}`;
  return `${formatResultInput(amount)} ${label}`;
};

const formatCoefficientDisplay = (coefficient: string): string => {
  const trimmed = String(coefficient ?? "").trim();
  if (!trimmed) return "x0.00";
  return trimmed.startsWith("x") || trimmed.startsWith("X") ? trimmed : `x${trimmed}`;
};

const isIncomeCurrency = (value: string): value is IncomeCurrency =>
  (CURRENCY_OPTIONS as readonly string[]).includes(value);

/** Convert open.er-api USD-base rates → USD per 1 unit of currency. */
const mapUsdBaseRatesToUsdPerUnit = (rates: Record<string, number>): Record<IncomeCurrency, number> => {
  const next: Record<IncomeCurrency, number> = { ...FALLBACK_USD_PER_UNIT };
  CURRENCY_OPTIONS.forEach((currency) => {
    if (currency === "USD") {
      next.USD = 1;
      return;
    }
    const unitsPerUsd = rates[currency];
    if (typeof unitsPerUsd === "number" && Number.isFinite(unitsPerUsd) && unitsPerUsd > 0) {
      next[currency] = 1 / unitsPerUsd;
    }
  });
  return next;
};

const fetchUsdPerUnitRates = async (): Promise<Record<IncomeCurrency, number>> => {
  try {
    const response = await fetch("https://open.er-api.com/v6/latest/USD");
    if (!response.ok) return FALLBACK_USD_PER_UNIT;
    const data = (await response.json()) as { result?: string; rates?: Record<string, number> };
    if (data.result !== "success" || !data.rates) return FALLBACK_USD_PER_UNIT;
    return mapUsdBaseRatesToUsdPerUnit(data.rates);
  } catch {
    return FALLBACK_USD_PER_UNIT;
  }
};

const CustomIncomeManagement = ({
  title,
  mode,
  accountingId,
  records = [],
  invoiceOptions = [],
  onRefresh,
  onClose,
}: Props) => {
  const { notify } = usePushNotifications();
  const formatDateDisplay = useFormatDateDisplay();
  const dateInputRef = useRef<HTMLInputElement | null>(null);

  const [usdPerUnit, setUsdPerUnit] = useState<Record<IncomeCurrency, number>>(FALLBACK_USD_PER_UNIT);
  const [form, setForm] = useState<IncomeFormState>(() => emptyForm());
  const [isHistoryOpen, setIsHistoryOpen] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coefficientTouched, setCoefficientTouched] = useState(false);

  const resolvedInvoiceOptions = useMemo(() => {
    const fromProps = invoiceOptions.map((item) => item.trim()).filter(Boolean);
    if (fromProps.length > 0) return Array.from(new Set(fromProps));
    return ["23059-I/01", "23059-I/02", "23059-I/03"];
  }, [invoiceOptions]);

  const historyTotal = useMemo(
    () => formatOfficeAccountingIncomeRecordsTotal(records) || formatOfficeAccountingUsdAmount(0),
    [records],
  );

  useEffect(() => {
    let cancelled = false;
    void fetchUsdPerUnitRates().then((rates) => {
      if (cancelled) return;
      setUsdPerUnit(rates);
      setForm((prev) => {
        if (coefficientTouched) return prev;
        const currency = isIncomeCurrency(prev.currency) ? prev.currency : "USD";
        const nextCoefficient = formatCoefficientInput(rates[currency]);
        if (prev.coefficient === nextCoefficient) return prev;
        return { ...prev, coefficient: nextCoefficient };
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load rates once on mount
  }, []);

  // Currency select drives the USD rate into multiplier (unless user edited it).
  useEffect(() => {
    if (coefficientTouched) return;
    const currency = isIncomeCurrency(form.currency) ? form.currency : "USD";
    const nextCoefficient = formatCoefficientInput(usdPerUnit[currency]);
    setForm((prev) => (prev.coefficient === nextCoefficient ? prev : { ...prev, coefficient: nextCoefficient }));
  }, [form.currency, usdPerUnit, coefficientTouched]);

  // Result in USD = value × rate (coefficient).
  useEffect(() => {
    const count = parseNumericInput(form.count);
    const coefficient = parseNumericInput(form.coefficient);
    if (count == null || coefficient == null) {
      setForm((prev) => (prev.result === "" ? prev : { ...prev, result: "" }));
      return;
    }
    const nextResult = formatResultInput(count * coefficient);
    setForm((prev) => (prev.result === nextResult ? prev : { ...prev, result: nextResult }));
  }, [form.count, form.coefficient]);

  const handleOpenDatePicker = () => {
    if (!dateInputRef.current) return;
    if (typeof dateInputRef.current.showPicker === "function") {
      dateInputRef.current.showPicker();
    } else {
      dateInputRef.current.focus();
      dateInputRef.current.click();
    }
  };

  const handleCurrencyChange = (currency: string) => {
    setCoefficientTouched(false);
    const resolved = isIncomeCurrency(currency) ? currency : "USD";
    setForm((prev) => ({
      ...prev,
      currency: resolved,
      coefficient: formatCoefficientInput(usdPerUnit[resolved]),
    }));
  };

  const handleClearForm = () => {
    if (!window.confirm("Clear the entered income values?")) return;
    setCoefficientTouched(false);
    setForm(emptyForm(usdPerUnit));
  };

  const handleApprove = async () => {
    if (!accountingId || accountingId <= 0) {
      notify({ title: "Save failed", message: "Accounting record id is missing.", type: "error" });
      return;
    }
    if (isSubmitting) return;

    const payload: IncomeFormState = {
      date: form.date.trim(),
      invoice_number: form.invoice_number.trim(),
      count: form.count.trim().replace(/\s/g, ""),
      currency: form.currency.trim() || "USD",
      coefficient: form.coefficient.trim().replace(/^x/i, "").replace(/\s/g, ""),
      account: form.account.trim(),
      result: form.result.trim().replace(/\s/g, ""),
      note: form.note.trim(),
    };

    console.groupCollapsed(`[Income] create ${mode} (applicant_accounting_id=${accountingId})`);
    console.log("payload:", { ...payload, applicant_accounting_id: accountingId });
    console.groupEnd();

    setIsSubmitting(true);
    try {
      const result =
        mode === "invitation"
          ? await officeAccountingPageRequests.createInvitationFeePaid(accountingId, payload)
          : await officeAccountingPageRequests.createPaymentFactUsd(accountingId, payload);

      if (!result.ok) {
        notify({
          title: "Save failed",
          message: result.message || "Could not create income record.",
          type: "error",
        });
        return;
      }

      notify({
        title: "Saved",
        message: mode === "invitation" ? "Invitation fee payment added." : "Payment fact added.",
        type: "success",
      });
      setCoefficientTouched(false);
      setForm(emptyForm(usdPerUnit));
      await onRefresh?.();
    } catch (error) {
      console.error("CustomIncomeManagement approve error:", error);
      notify({ title: "Save failed", message: "Could not create income record.", type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="custom-income">
      <div className="custom-income-title">
        <div className="custom-income-title-text">
          <p>{title}</p>
        </div>
        <div className="custom-income-title-close" onClick={onClose}>
          <CloseSvg />
        </div>
      </div>
      <div className="custom-income-calc">
        <div className="custom-income-calc-date" onClick={handleOpenDatePicker}>
          <input
            ref={dateInputRef}
            type="date"
            value={form.date}
            onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
          />
          <CalendarSvg />
        </div>
        <div className="custom-income-calc-value">
          <div className="custom-income-calc-value-input">
            <input
              type="text"
              placeholder="0.00"
              value={form.count}
              onChange={(e) => setForm((prev) => ({ ...prev, count: e.target.value }))}
            />
          </div>
          <div className="custom-income-calc-value-select">
            <select value={form.currency} onChange={(e) => handleCurrencyChange(e.target.value)}>
              {CURRENCY_OPTIONS.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
            <ArrowSvg />
          </div>
        </div>
        <div className="custom-income-calc-multiplier">
          <input
            type="text"
            placeholder="x0.00"
            value={form.coefficient}
            onChange={(e) => {
              setCoefficientTouched(true);
              const raw = e.target.value;
              const withPrefix = raw === "" || raw.startsWith("x") || raw.startsWith("X") ? raw : `x${raw}`;
              setForm((prev) => ({ ...prev, coefficient: withPrefix }));
            }}
          />
          <div className="custom-income-calc-multiplier-line"></div>
          <MultiplierSvg />
        </div>
        <div className="custom-income-calc-result">
          <div className="custom-income-calc-result-title">
            <p>Result:</p>
          </div>
          <div className="custom-income-calc-result-input">
            <input type="text" placeholder="0.00" value={form.result} readOnly />
            <p>USD</p>
          </div>
        </div>
      </div>
      <div className="custom-income-selectors">
        <div className="custom-income-selectors-item">
          <div className="custom-income-selectors-item-label">
            <p>Invoice No</p>
          </div>
          <div className="custom-income-selectors-item-select">
            <select
              value={form.invoice_number}
              onChange={(e) => setForm((prev) => ({ ...prev, invoice_number: e.target.value }))}
            >
              <option value="">Please Choose Invoice</option>
              {resolvedInvoiceOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <ArrowSvg />
          </div>
        </div>
        <div className="custom-income-selectors-item">
          <div className="custom-income-selectors-item-label">
            <p>To Account:</p>
          </div>
          <div className="custom-income-selectors-item-select">
            <select value={form.account} onChange={(e) => setForm((prev) => ({ ...prev, account: e.target.value }))}>
              <option value="">Please Choose</option>
              {ACCOUNT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <ArrowSvg />
          </div>
        </div>
      </div>
      <div className="custom-income-note">
        <textarea
          placeholder="Note..."
          value={form.note}
          onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
        />
      </div>
      <div className="custom-income-history">
        <div className="custom-income-history-header">
          <div className="custom-income-history-header-label">
            <p>History</p>
          </div>
          <div className="custom-income-history-header-icons">
            <div
              className={`custom-income-history-header-icons-approve ${isSubmitting ? "disabled" : ""}`}
              onClick={() => {
                if (!isSubmitting) void handleApprove();
              }}
            >
              <ApproveSvg />
            </div>
            <div className="custom-income-history-header-icons-close" onClick={handleClearForm}>
              <CloseSvg />
            </div>
            <div
              className={`custom-income-history-header-icons-arrow ${isHistoryOpen ? "open" : ""}`}
              onClick={() => setIsHistoryOpen((prev) => !prev)}
            >
              <ArrowSvg />
            </div>
          </div>
        </div>
        {isHistoryOpen && (
          <div className="custom-income-history-list">
            {records.length === 0 ? (
              <div className="custom-income-history-list-empty">
                <p>No records yet.</p>
              </div>
            ) : (
              records.map((record, index) => {
                const key = record.id ?? `${record.date ?? ""}-${record.invoice_number ?? ""}-${index}`;
                const dateLabel = record.date ? formatDateDisplay(record.date, { dateOnly: true }) : "—";
                return (
                  <div className="custom-income-history-list-item" key={key}>
                    <div className="custom-income-history-list-item-main">
                      <div className="custom-income-history-list-item-main-date">
                        <p>{dateLabel}</p>
                      </div>
                      <div className="custom-income-history-list-item-main-invoice">
                        <p>{record.invoice_number || "—"}</p>
                      </div>
                      <div className="custom-income-history-list-item-main-value">
                        <p>{formatCountDisplay(record.count ?? "", record.currency ?? "USD")}</p>
                      </div>
                    </div>
                    <div className="custom-income-history-list-item-multiplier">
                      <p>{formatCoefficientDisplay(record.coefficient ?? "")}</p>
                    </div>
                    <div className="custom-income-history-list-item-result">
                      <div className="custom-income-history-list-item-result-label">
                        <p>{record.account || "—"}</p>
                      </div>
                      <div className="custom-income-history-list-item-result-value">
                        <p>
                          {record.result
                            ? formatOfficeAccountingUsdAmount(parseOfficeAccountingAmount(record.result))
                            : "0.00 USD"}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
        <div className="custom-income-history-total">
          <p>Total</p>
          <p>{historyTotal}</p>
        </div>
      </div>
    </div>
  );
};

export default CustomIncomeManagement;
